import { useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";

// API
import { emptyCart } from "../api/cart";

// Stripe
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

// Context
import AlertContext from "../contexts/AlertContext";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";

// Bootstrap components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

// Custom styles
import "../scss/pages/cart.styles.scss";

// Assets
// import product from "../assets/img/product-individual.jpg";
import deleteIcon from "../assets/img/delete-icon--grey.svg";

// Utils
import formatMoney from "../utils/formatMoney";

// Custom components
import PaymentForm from "../components/PaymentForm/PaymentForm";

// Hooks
import { useCartValidation } from "../hooks/useCartValidation";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLIC_KEY,
  {
    locale: "es-419",
  }
);

export default function Cart() {
  const {
    cartItems,
    getTotalPriceCart,
    guestRemoveProduct,
    setCartItems,
    addProductToCart,
    guestEmptyCart,
    emptyUserCart,
    refreshCartPrices,
    isInitializing,
  } = useCart();

  // Cart validation hook
  const { validatedCart, isValidating, validateCartItems } = useCartValidation(cartItems);

  // Check if any products are unavailable (only when validation is complete)
  const hasUnavailableProducts = !isValidating && (validatedCart?.some(item => !item.isAvailable) || false);

  // Debug logging
  // console.log('Cart.js: cartItems from context:', cartItems);
  // console.log('Cart.js: validatedCart from hook:', validatedCart);
  // console.log('Cart.js: isValidating:', isValidating);
  // console.log('Cart.js: isInitializing:', isInitializing);

  const [shipment, setShipment] = useState({
    shipmentCost: 0,
    shipmentType: "",
  });
  const { currentUser } = useAuth();
  // console.log(currentUser);

  const [loading, setLoading] = useState(false);
  const [productEditLoading, setProductEditLoading] = useState(false);

  const alert = useContext(AlertContext);

  // Auto-refresh cart prices when user becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && validatedCart?.length > 0) {
        validateCartItems(true);
      }
    };

    const handleFocus = () => {
      if (validatedCart?.length > 0) {
        validateCartItems(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [validatedCart, validateCartItems]);

  // const [cartItems, setCartItems] = useState(0);

  // useEffect(() => {
  //   (async () => {
  //     // setLoading(true);
  //     if (Object.keys(currentUser).length !== 0) {
  //       const response = await getCart(currentUser.localId);
  //       if (response !== null) {
  //         setCartItems(response);
  //         // setLoading(false);
  //       } else setCartItems([]);
  //     }
  //   })();
  // }, [currentUser]);
  // const appearance = {
  //   theme: "stripe",
  // };
  // const options = {
  //   appearance,
  // };

  // const options = {
  //   fonts: [
  //     {
  //       cssSrc: "https://fonts.googleapis.com/css2?family=Raleway&display=swap",
  //     },
  //   ],
  // };

  // Show loading state while cart is initializing
  if (isInitializing) {
    return (
      <div className="cart__wrapper">
        <Container>
          <div className="cart__loading">
            <h4>Cargando carrito...</h4>
            <p>Espera mientras cargamos tus productos...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="cart__wrapper">
      <div
        className={`overlay ${loading || productEditLoading ? "show" : null}`}
      ></div>
      <Container>
        <div className="cart__header">
          <h4>Tu orden y pago</h4>
        </div>
        <Row>
          <Col xs="12" lg="5" xl="4">
            <div className="cart__order-summary">
              <div className="cart__order-summary__header">
                <h5>Tu orden</h5>
              </div>
              <div className="cart__order-summary__body">
                {isValidating || (cartItems?.length > 0 && (!validatedCart || validatedCart.length === 0)) ? (
                  <div className="cart__order-summary__validating">
                    <p className="p-3 text-center">Actualizando precios y disponibilidad...</p>
                  </div>
                ) : (validatedCart?.length > 0 || cartItems?.length > 0) ? (
                  (validatedCart?.length > 0 ? validatedCart : cartItems)?.map((cartItem, key) => (
                    <div className={`cart__order-summary__product ${!cartItem.isAvailable ? 'unavailable' : ''}`} key={key}>
                      {cartItem.retroGameID ? (
                        <img
                          src={`https://gamerscode.mx/img/retrogames/${cartItem.retroGameID}.JPG`}
                          alt="Gamerscode"
                          className="img-fluid cart__order-summary__product__media"
                        />
                      ) : (
                        <img
                          src={`https://gamerscode.mx/img/products/${cartItem.ID_Info}.jpg`}
                          alt="Gamerscode"
                          className="img-fluid cart__order-summary__product__media"
                        />
                      )}

                      <div className="cart__order-summary__product__info">
                        <h5>{cartItem.Name}</h5>
                        <div className="cart__order-summary__product__info__specs">
                          <div className="flex-column">
                            <span>{cartItem.typeOfBox}</span>
                            <h6>
                              Cantidad: <span>{cartItem.Quantity}</span>
                            </h6>
                          </div>
                          <div className="cart__order-summary__product__info__specs__price">
                            <h6 className={cartItem.hasDiscount ? 'discount-price' : 'regular-price'}>
                              {formatMoney(cartItem.currentPrice || cartItem.Sale)}
                              {/* {cartItem.currentPrice !== cartItem.Sale && (
                                <span className="price-changed">Precio actualizado</span>
                              )} */}
                            </h6>
                            {cartItem.hasDiscount && (
                              <span className="original-price">
                                {formatMoney(cartItem.originalPrice)}
                              </span>
                            )}
                            {!cartItem.isAvailable && (
                              <span className="out-of-stock">Agotado</span>
                            )}
                            {cartItem.validationError && (
                              <span className="validation-error">Error de validación</span>
                            )}
                            {/* {cartItem.locationStatus && cartItem.locationStatus.length > 0 && (
                              <div className="cart-location-details">
                                {cartItem.locationStatus.map((location, index) => (
                                  <span
                                    key={index}
                                    className={`cart-location-badge ${location.hasStock ? 'available' : 'unavailable'}`}
                                  >
                                    {location.name}: {location.status}
                                  </span>
                                ))}
                              </div>
                            )} */}
                            <button
                              onClick={async () => {
                                if (currentUser.localId) {
                                  setProductEditLoading(true);
                                  if (cartItems?.length === 1) {
                                    const response = await emptyCart(
                                      currentUser.localId
                                    );
                                    if (response.status === 200) {
                                      emptyUserCart();
                                      setProductEditLoading(false);
                                      alert.success("Producto eliminado");
                                    } else {
                                      alert.error(
                                        "Error al eliminar producto de tu bolsa. Inténtalo de nuevo"
                                      );
                                    }
                                    return;
                                  }
                                  setCartItems([
                                    ...cartItems?.filter(
                                      (item) =>
                                        item.ID_Info !== cartItem.ID_Info
                                    ),
                                  ]);
                                  const response = await addProductToCart(
                                    currentUser.localId,
                                    [
                                      ...cartItems?.filter(
                                        (item) =>
                                          item.ID_Info !== cartItem.ID_Info
                                      ),
                                    ]
                                  );
                                  if (response.status === 200) {
                                    // console.log("eliminado");
                                    alert.success("Producto eliminado");
                                    setProductEditLoading(false);
                                  }
                                  // if (cartItems?.length === 0) {
                                  //   addProductToCart(
                                  //     currentUser.localId,
                                  //     cartItems
                                  //   );
                                  //   history.push("/cart");
                                  // } else {
                                  //   addProductToCart(currentUser.localId, [
                                  //     ...cartItems,
                                  //     {
                                  //       // ...product.detail,
                                  //       ID_Info: id,
                                  //       Name: product.detail.Name,
                                  //       Platform: product.detail.Platform,
                                  //       Purchase: `${product.detail.Purchase}`,
                                  //       Exchange: `${product.detail.Exchange}`,
                                  //       Sale: `${product.detail.Sale}`,
                                  //       Quantity: "1",
                                  //     },
                                  //   ]);
                                  //   history.push("/cart");
                                  // }
                                  // console.log("usuario registrado");
                                } else if (currentUser.guest === true) {
                                  guestRemoveProduct(cartItem);
                                }
                              }}
                            >
                              <img src={deleteIcon} alt="Gamerscode" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4">No hay elementos en tu carrito</div>
                )}
              </div>
              {(validatedCart?.length > 0 || cartItems?.length > 0) && (
                <div className="cart__order-summary__keep">
                  <Link
                    to={`/catalogue/game/${(validatedCart?.length > 0 ? validatedCart : cartItems)[(validatedCart?.length > 0 ? validatedCart : cartItems)?.length - 1]?.Platform}`}
                  >
                    Seguir comprando
                  </Link>
                </div>
              )}
              <div className="cart__order-summary__total">
                <div>
                  <h6>Total de piezas:</h6>
                  <h5>
                    {(validatedCart?.length > 0 ? validatedCart : cartItems)?.length} <span>pza(s)</span>
                  </h5>
                </div>
                <div>
                  <h6>Envío:</h6>
                  <h5>
                    {getTotalPriceCart(validatedCart?.length > 0 ? validatedCart : cartItems) < 999
                      ? shipment.shipmentCost === 0
                        ? "Escoge un tipo de envío"
                        : formatMoney(shipment.shipmentCost)
                      : "Gratis"}
                  </h5>
                </div>
                <div>
                  <h4>Total:</h4>
                  <h3>
                    {formatMoney(
                      getTotalPriceCart(validatedCart?.length > 0 ? validatedCart : cartItems) + shipment.shipmentCost
                    )}
                  </h3>
                </div>
                {(validatedCart?.length > 0 || cartItems?.length > 0) ? (
                  <p
                    onClick={async () => {
                      if (currentUser.localId) {
                        setProductEditLoading(true);
                        const response = await emptyCart(currentUser.localId);
                        if (response.status === 200) {
                          emptyUserCart();
                          setProductEditLoading(false);
                          alert.success("Productos eliminados");
                        } else {
                          alert.error(
                            "Error al eliminar productos de tu bolsa. Inténtalo de nuevo"
                          );
                        }
                        return;
                      }
                      if (currentUser.guest === true) {
                        setCartItems([]);
                        localStorage.setItem("cartItems", []);
                      }
                    }}
                  >
                    Vaciar carrito
                  </p>
                ) : (
                  <></>
                )}
              </div>
            </div>
          </Col>
          <Col xs="12" lg="7" xl="8">
            {hasUnavailableProducts && (
              <div className="cart__unavailable-warning mb-3 p-3" style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                color: '#856404'
              }}>
                <h6 style={{ marginBottom: '0.5rem', fontWeight: '600' }}>
                  ⚠️ Productos no disponibles
                </h6>
                <p style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Algunos productos en tu carrito no están disponibles. Por favor elimina los productos marcados como "Agotado" antes de proceder al pago.
                </p>
              </div>
            )}
            <Elements stripe={stripePromise}>
              <PaymentForm
                shipment={shipment}
                setShipment={setShipment}
                totalPriceCart={
                  getTotalPriceCart(validatedCart?.length > 0 ? validatedCart : cartItems) + shipment.shipmentCost
                }
                loading={loading}
                setLoading={setLoading}
                currentUser={currentUser}
                guestEmptyCart={guestEmptyCart}
                emptyUserCart={emptyUserCart}
                disabled={hasUnavailableProducts}
              />
            </Elements>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
